import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import { logCount } from '@/utils/logger/arms-config';
import { readListeningAudioPosition, writeListeningAudioPosition } from './listeningAudioCheckpoint';
import type { ListeningAudioPosition } from './listeningAudioCheckpoint';

export const AUDIO_STALL_TIMEOUT_MS = 15_000;
export type ExamAudioStatus = 'loading' | 'playing' | 'buffering' | 'offline' | 'error' | 'blocked' | 'finished' | 'stopped';

interface Options {
  audioRef: RefObject<HTMLAudioElement>;
  active: boolean;
  examKey: string;
  sources: string[];
  audioIndex: number;
  startTime: number;
  blockedRef: MutableRefObject<boolean>;
  onIndexChange: (index: number) => void;
  onTimeChange: (seconds: number) => void;
}

export function useExamListeningAudio(options: Options) {
  const { audioRef, active, examKey, audioIndex, startTime, blockedRef } = options;
  const sourcesKey = JSON.stringify(options.sources);
  const callbacks = useRef(options);
  callbacks.current = options;
  const localPosition = useRef<{examKey: string; sourcesKey: string; position: ListeningAudioPosition;}>();
  const retryRef = useRef<(() => void) | undefined>();
  const [status, setStatus] = useState<ExamAudioStatus>('loading');
  const retry = useCallback(() => retryRef.current?.(), []);

  useEffect(() => {
    const audio = audioRef.current;
    const sources: string[] = JSON.parse(sourcesKey);
    if (!active || !audio || !sources.length || blockedRef.current) {
      if (blockedRef.current) setStatus('stopped');
      return;
    }

    const previous = localPosition.current;
    const sameExam = previous?.examKey === examKey && previous.sourcesKey === sourcesKey;
    const fallback = sameExam && previous.position.audioIndex >= audioIndex
      ? previous.position
      : {audioIndex, currentTime: audioIndex === sources.length || sameExam ? 0 : startTime};
    const position = readListeningAudioPosition(examKey, sources, fallback);
    localPosition.current = {examKey, sourcesKey, position};
    const persist = () => writeListeningAudioPosition(examKey, sources, position);
    persist();
    if (position.audioIndex !== audioIndex) {
      callbacks.current.onTimeChange(position.currentTime);
      callbacks.current.onIndexChange(position.audioIndex);
      return;
    }
    if (position.audioIndex === sources.length) {
      setStatus('finished');
      return;
    }

    let disposed = false;
    let restoring = true;
    let attempt = 0;
    let lastProgressAt = Date.now();
    let currentStatus: ExamAudioStatus = 'loading';
    const isBlocked = () => disposed || blockedRef.current;
    const updateStatus = (next: ExamAudioStatus) => {
      currentStatus = next;
      if (!disposed) setStatus(next);
    };
    const snapshot = () => {
      // load() resets currentTime to zero. Never replace the resume point while
      // the new resource is still loading or being restored.
      if (restoring || !Number.isFinite(audio.currentTime)) return;
      if (audio.currentTime > position.currentTime) {
        position.currentTime = audio.currentTime;
        lastProgressAt = Date.now();
      }
      persist();
      callbacks.current.onTimeChange(position.currentTime);
    };
    const fail = (next: 'error' | 'blocked') => {
      if (isBlocked()) return;
      snapshot();
      updateStatus(next === 'blocked' ? next : navigator.onLine === false ? 'offline' : next);
    };
    const play = () => {
      if (isBlocked()) return;
      const currentAttempt = attempt;
      try {
        audio.play()?.catch((error: {name?: string; code?: number;}) => {
          if (isBlocked() || currentAttempt !== attempt) return;
          if (error?.name === 'NotAllowedError' || error?.code === 0) {
            fail('blocked');
          } else {
            fail('error');
          }
        });
      } catch {
        fail('error');
      }
    };
    const onMetadata = () => {
      if (isBlocked() || !restoring) return;
      try {
        const target = Number.isFinite(audio.duration)
          ? Math.min(position.currentTime, Math.max(0, audio.duration - 0.05))
          : position.currentTime;
        audio.currentTime = target;
        position.currentTime = target;
        persist();
        restoring = false;
        lastProgressAt = Date.now();
        callbacks.current.onTimeChange(target);
        play();
      } catch {
        fail('error');
      }
    };
    const reload = () => {
      if (isBlocked() || position.audioIndex >= sources.length) return;
      snapshot();
      restoring = true;
      attempt += 1;
      lastProgressAt = Date.now();
      updateStatus('loading');
      audio.src = sources[position.audioIndex];
      audio.load();
    };
    const onProgress = () => {
      if (isBlocked() || restoring) return;
      snapshot();
      if (!audio.paused && !audio.ended) updateStatus('playing');
    };
    const onPlaying = () => {
      if (isBlocked() || restoring) return;
      lastProgressAt = Date.now();
      updateStatus('playing');
    };
    const onWaiting = () => {
      if (!isBlocked() && !['offline', 'error', 'blocked'].includes(currentStatus)) updateStatus('buffering');
    };
    const onError = () => {
      if (isBlocked()) return;
      logCount(`Mock_Listening_Audio_Error_${audio.error?.code || 0}`);
      fail('error');
    };
    const onPause = () => {
      if (isBlocked() || restoring || audio.ended) return;
      snapshot();
      if (currentStatus === 'playing') fail('error');
    };
    const onEnded = () => {
      if (isBlocked() || restoring) return;
      position.audioIndex += 1;
      position.currentTime = 0;
      restoring = true;
      persist();
      callbacks.current.onTimeChange(0);
      callbacks.current.onIndexChange(position.audioIndex);
      updateStatus(position.audioIndex === sources.length ? 'finished' : 'loading');
    };
    const onOnline = () => {
      if (!isBlocked() && (currentStatus === 'offline' || currentStatus === 'error')) reload();
    };
    const onOffline = () => {
      snapshot();
      // Buffered audio can still play offline. Do not interrupt it merely
      // because the operating system reports a connectivity change.
      if (!isBlocked() && (audio.paused || restoring || currentStatus === 'error')) updateStatus('offline');
    };
    const events = {
      loadedmetadata: onMetadata, timeupdate: onProgress, playing: onPlaying,
      waiting: onWaiting, stalled: onWaiting, error: onError, pause: onPause, ended: onEnded,
    };
    Object.entries(events).forEach(([event, handler]) => audio.addEventListener(event, handler));
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('pagehide', snapshot);
    retryRef.current = () => {
      if (isBlocked() || !['error', 'offline', 'blocked'].includes(currentStatus)) return;
      logCount('Mock_Listening_Audio_Retry');
      // A user gesture must call play synchronously for autoplay permission.
      if (currentStatus === 'blocked' && !restoring && !audio.error) {
        lastProgressAt = Date.now();
        updateStatus('loading');
        play();
      }
      else reload();
    };
    const watchdog = setInterval(() => {
      if (disposed || currentStatus === 'finished') return;
      if (blockedRef.current) {
        if (currentStatus !== 'stopped') updateStatus('stopped');
        return;
      }
      if (!restoring && audio.currentTime > position.currentTime) onProgress();
      if (Date.now() - lastProgressAt >= AUDIO_STALL_TIMEOUT_MS && !['error', 'offline', 'blocked'].includes(currentStatus)) {
        logCount('Mock_Listening_Audio_Stalled');
        fail('error');
      }
    }, 1000);
    reload();

    return () => {
      snapshot();
      disposed = true;
      attempt += 1;
      retryRef.current = undefined;
      clearInterval(watchdog);
      Object.entries(events).forEach(([event, handler]) => audio.removeEventListener(event, handler));
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('pagehide', snapshot);
      audio.pause();
    };
  }, [active, examKey, sourcesKey, audioIndex, startTime, audioRef, blockedRef]);

  return {status, retry};
}
